import {
  buildBasecampWebUrl,
  normalizeBasecampWebHost,
} from '@/lib/basecamp-external-url'
import type { NavigateFunction } from "react-router-dom"
import type { Dispatch, MouseEvent } from "react"
import type { ChangedLines } from "../../lib/api"
import {
  displaySegmentsForStoragePath,
  documentUrlForPathOrId,
  isUuid,
  type WorkspaceRouteInfo,
} from "@/lib/app-routes"
import { markProgrammaticNavigation } from "@/hooks/use-url-sync"
import { resolveOpenIntent } from "@/lib/open-intent"
import type { PerTabNavigationApi } from "@/hooks/use-navigation-history"
import type { BreadcrumbSegment } from "../ui/address-bar"
import type { OpenTab } from "../features/document-tabs"
import { isBasecampIntegrationTabId } from "@/lib/basecamp-tab-routing"
import { isPlatformSkillTabId } from "@/lib/open-platform-skill"

/** Basecamp ids for toolbar actions on integration todo tabs (no document frontmatter). */
export function toolbarBasecampLinkage(
  currentTab: OpenTab | undefined,
  basecampTodoId: string | null,
  basecampProjectId: string | null
): { todoId: string | null; projectId: string | null } {
  if (currentTab?.type === 'integration-basecamp-todo') {
    const todoId =
      currentTab.integrationData?.todoId ??
      currentTab.id.replace(/^basecamp-todo-/, '') ??
      null
    const projectId = currentTab.integrationData?.projectId ?? null
    return {
      todoId: todoId || basecampTodoId,
      projectId: projectId || basecampProjectId,
    }
  }
  return { todoId: basecampTodoId, projectId: basecampProjectId }
}

const DOCUMENT_FILE_EXT_RE = /\.(md|html|csv|txt|json)$/i

/**
 * Cold-load / URL-sync provisional tabs use the folder path as id.
 * Those are not documents - fetching them 404-spams and can loop setState.
 */
export function isPathKeyedUnresolvedDocumentId(
  id: string | null | undefined,
): boolean {
  if (!id || !id.includes('/')) return false
  if (isUuid(id)) return false
  const base = id.split('/').pop() || ''
  return !DOCUMENT_FILE_EXT_RE.test(base)
}

/**
 * Extensionless URLs (README, not README.md) share this check with real folders.
 * Resolve first. A document opens by id. Only a folder goes to explorer.
 */
export function openTargetForUnresolvedPath(
  kind: 'document' | 'folder' | 'missing',
  ids: { id?: string; path?: string },
):
  | { contentId: string; params: { type: 'document' } }
  | { contentId: 'list'; params: { type: 'explorer'; folderPath: string } }
  | null {
  if (kind === 'document' && ids.id) {
    return { contentId: ids.id, params: { type: 'document' } }
  }
  if (kind === 'folder' && ids.path) {
    return { contentId: 'list', params: { type: 'explorer', folderPath: ids.path } }
  }
  return null
}

/** Tab ids for editor-hosted agent chat surfaces (not real documents). */
export function isChatConversationTabId(tabId: string | null | undefined): boolean {
  if (!tabId) return false
  return tabId.startsWith('chat-conversation-')
}

/** Tabs whose content is not backed by a local editable document file. */
export function shouldSkipExternalDocumentPolling(
  activeTabId: string | null | undefined,
  tab: OpenTab | undefined,
): boolean {
  return !isContentDocumentTab(tab, activeTabId)
}

/** Tabs that should clear toolbar document path metadata immediately. */
export function shouldClearDocumentPathForTab(
  activeTabId: string | null | undefined,
  tab: OpenTab | undefined,
): boolean {
  if (!activeTabId) return true
  return (
    activeTabId === 'list' ||
    activeTabId === 'home' ||
    activeTabId === 'search-view' ||
    tab?.type === 'person-view' ||
    activeTabId === 'edit-history' ||
    activeTabId === 'view:edit-history' ||
    tab?.type === 'explorer' ||
    isPathKeyedUnresolvedDocumentId(activeTabId) ||
    activeTabId === 'graph' ||
    tab?.type === 'graph-view' ||
    tab?.type === 'home' ||
    tab?.type === 'workspace-home' ||
    tab?.type === 'desk-home' ||
    tab?.type === 'edit-history' ||
    tab?.type === 'inbox' ||
    tab?.type === 'inbox-thread' ||
    isChatConversationTabId(activeTabId) ||
    tab?.type === 'chat-conversation' ||
    tab?.type === 'search-view' ||
    isBasecampIntegrationTabId(activeTabId) ||
    isPlatformSkillTabId(activeTabId) ||
    tab?.type === 'platform-skill' ||
    activeTabId.startsWith('integration-')
  )
}

/** Special app/integration tabs that must not be fetched through document APIs. */
export function shouldSkipDocumentPathFetch(
  activeTabId: string | null | undefined,
  tab: OpenTab | undefined,
): boolean {
  if (!activeTabId) return true
  return (
    activeTabId === 'list' ||
    activeTabId === 'graph' ||
    activeTabId === 'home' ||
    activeTabId === 'inbox' ||
    activeTabId === 'search-view' ||
    activeTabId.startsWith('inbox-thread-') ||
    isChatConversationTabId(activeTabId) ||
    tab?.type === 'chat-conversation' ||
    tab?.type === 'inbox' ||
    tab?.type === 'inbox-thread' ||
    tab?.type === 'search-view' ||
    tab?.type === 'person-view' ||
    tab?.type === 'explorer' ||
    isPathKeyedUnresolvedDocumentId(activeTabId) ||
    activeTabId === 'people-index' ||
    activeTabId.startsWith('person-view-') ||
    activeTabId.startsWith('home-') ||
    activeTabId.startsWith('workspace-home-') ||
    activeTabId === 'edit-history' ||
    activeTabId === 'view:edit-history' ||
    isBasecampIntegrationTabId(activeTabId) ||
    isPlatformSkillTabId(activeTabId) ||
    tab?.type === 'platform-skill' ||
    activeTabId.startsWith('integration-') ||
    activeTabId.startsWith('gdrive-preview-')
  )
}

/**
 * Basenames that end in `-board` but are roadmap session docs, not Product Board configs.
 * @see docs/product/roadmap/STATUS-BOARD.md (parsed by GET /api/roadmap, not a board doc)
 */
const NON_PRODUCT_BOARD_BOARD_BASENAMES = new Set(['status-board'])

/**
 * Matches app-doc Product Board config documents (e.g. product-board.md, business-board.md,
 * or extensionless v2 paths like flownotes-app-docs/business/business-board).
 * Does not match STATUS-BOARD or other roadmap markdown that happens to end in `-board`.
 */
export function isMarkdownProductBoardFilePath(filePathOrBasename: string): boolean {
  const s = filePathOrBasename.trim().replace(/\\/g, '/')
  const basename = (s.split('/').pop() ?? s).replace(/\.md$/i, '')
  if (!basename) return false
  if (NON_PRODUCT_BOARD_BOARD_BASENAMES.has(basename.toLowerCase())) return false
  return /^[a-zA-Z0-9][a-zA-Z0-9._-]*-board$/i.test(basename)
}

/**
 * Format a date as a human-readable "time ago" string
 */
export function formatTimeAgo(date: Date): string {
  const now = new Date()
  const diffMs = now.getTime() - date.getTime()
  const diffSecs = Math.floor(diffMs / 1000)
  const diffMins = Math.floor(diffSecs / 60)
  const diffHours = Math.floor(diffMins / 60)
  const diffDays = Math.floor(diffHours / 24)

  if (diffSecs < 10) return 'just now'
  if (diffSecs < 60) return `${diffSecs} second${diffSecs !== 1 ? 's' : ''} ago`
  if (diffMins < 60) return `${diffMins} minute${diffMins !== 1 ? 's' : ''} ago`
  if (diffHours < 24) return `${diffHours} hour${diffHours !== 1 ? 's' : ''} ago`
  if (diffDays < 7) return `${diffDays} day${diffDays !== 1 ? 's' : ''} ago`
  return date.toLocaleDateString()
}

/**
 * Normalize changed lines data from various formats
 */
export function normalizeChangedLines(raw: any): ChangedLines | null {
  if (!raw) return null
  let value = raw
  if (typeof value === 'string') {
    try {
      value = JSON.parse(value)
    } catch {
      return null
    }
  }
  if (!value || typeof value !== 'object') {
    return null
  }

  const ensureNumberArray = (arr: any): number[] => {
    if (!Array.isArray(arr)) return []
    return arr
      .map((item) => Number(item))
      .filter((item) => Number.isFinite(item) && item > 0)
  }

  return {
    added: ensureNumberArray(value.added),
    removed: ensureNumberArray(value.removed),
    modified: ensureNumberArray(value.modified),
    changed_at: value.changed_at,
    changed_by: value.changed_by,
    change_status: value.change_status,
    requires_acceptance: value.requires_acceptance,
    accepted_at: value.accepted_at ?? null,
    conversation_id: value.conversation_id ?? null,
  }
}

/**
 * Construct Basecamp URL from todo ID and project ID (app.basecamp.com).
 */
export function constructBasecampUrl(
  todoId: string,
  projectId: string,
  accountId: string = '5744549',
  recordType: 'todo' | 'todolist' = 'todo',
): string {
  return buildBasecampWebUrl(todoId, projectId, recordType, accountId)
}

/** Matches POST /api/basecamp/ingest-todo URL parser (buckets or projects segment). */
const BASECAMP_INGEST_TODO_URL_RE =
  /^https:\/\/(?:3|app)\.basecamp\.com\/\d+\/(?:buckets|projects)\/\d+\/todos\/\d+/

function toBasecampWebUrl(apiOrWebUrl: string): string {
  return apiOrWebUrl.replace('basecampapi.com', 'basecamp.com').replace(/\.json$/i, '')
}

function isIngestibleBasecampTodoUrl(url: string): boolean {
  const normalized = url.split('#')[0].split('?')[0]
  return BASECAMP_INGEST_TODO_URL_RE.test(normalized)
}

/**
 * Resolve a canonical Basecamp todo URL for ingest-todo API.
 * Avoids using todo.url when it is a project API URL or other non-todo link.
 */
export function resolveBasecampTodoIngestUrlFromTodo(
  todo: { app_url?: string; web_url?: string; url?: string },
  projectId: string,
  todoId: string
): string {
  const candidates: string[] = []
  if (todo.web_url) candidates.push(toBasecampWebUrl(todo.web_url))
  if (todo.app_url) candidates.push(toBasecampWebUrl(todo.app_url))
  if (todo.url) candidates.push(toBasecampWebUrl(todo.url))

  for (const candidate of candidates) {
    const normalized = candidate.split('#')[0].split('?')[0].replace(/\/+$/, '')
    if (isIngestibleBasecampTodoUrl(normalized)) {
      return normalized
    }
  }

  const accountMatch = (todo.app_url || todo.url || '').match(
    /\/\/(?:3|app)\.basecamp(?:api)?\.com\/(\d+)\//
  )
  const accountId = accountMatch ? accountMatch[1] : '5744549'
  return normalizeBasecampWebHost(constructBasecampUrl(todoId, projectId, accountId))
}

/**
 * Extract project ID from Basecamp URL
 */
export function extractProjectIdFromUrl(url: string): string | null {
  const urlMatch = url.match(/\/buckets\/(\d+)\//)
  return urlMatch ? urlMatch[1] : null
}

/**
 * Get current tab slot ID for navigation
 */
export function getCurrentTabSlotId(activeTabId: string, openTabs: OpenTab[]): string {
  const currentTab = openTabs.find(tab => tab.id === activeTabId)

  if (!currentTab?.tabSlotId) {
    if (import.meta.env.DEV) {
      console.debug('[DocumentViewer] Tab missing tabSlotId, using fallback:', { activeTabId })
    }
    return 'tab-main'
  }

  return currentTab.tabSlotId
}

export type BuildDocumentFolderBreadcrumbsOptions = {
  documentPath: string
  workspaces: WorkspaceRouteInfo[]
  /** Prepended before folder segments (e.g. basecamp-notes parent todo). */
  leadingSegments?: BreadcrumbSegment[]
  openTabs: OpenTab[]
  activeTabId: string
  setOpenTabs: Dispatch<React.SetStateAction<OpenTab[]>>
  setActiveTabId: (id: string) => void
  navigation: PerTabNavigationApi
  navigate: NavigateFunction
  onFolderClick?: (folderPath: string, skipTabManagement?: boolean, historySlotId?: string) => void
}

function createFolderBreadcrumbClickHandler(
  folderPath: string,
  opts: BuildDocumentFolderBreadcrumbsOptions
) {
  return (event?: MouseEvent) => {
    const currentTab = opts.openTabs.find(tab => tab.id === opts.activeTabId)
    const openIntent = resolveOpenIntent(event, { currentTab, activeTabId: opts.activeTabId })

    markProgrammaticNavigation()

    if (openIntent === 'new') {
      const newTabSlotId = `tab-${Date.now()}`
      const explorerTabId = `explorer-${Date.now()}`
      const newTab: OpenTab = {
        id: explorerTabId,
        title: 'Explorer',
        type: 'explorer',
        isDirty: false,
        isPinned: false,
        tabSlotId: newTabSlotId,
        explorerData: { folderPath },
      }
      opts.setOpenTabs([...opts.openTabs, newTab])
      opts.setActiveTabId(explorerTabId)
      opts.navigation.navigate(
        newTabSlotId,
        { documentId: null, folderPath, viewMode: 'list' },
        'breadcrumb-folder-new-tab',
      )
      opts.onFolderClick?.(folderPath, true, newTabSlotId)
    } else if (currentTab?.type === 'explorer') {
      const explorerNavSlot = currentTab?.tabSlotId || 'tab-main'
      const updatedTabs = opts.openTabs.map(tab =>
        tab.id === opts.activeTabId ? { ...tab, explorerData: { folderPath } } : tab,
      )
      opts.setOpenTabs(updatedTabs)
      opts.navigation.navigate(
        explorerNavSlot,
        { documentId: null, folderPath, viewMode: 'list' },
        'breadcrumb-folder-update-explorer',
      )
      opts.onFolderClick?.(folderPath)
    } else {
      // Switch to explorer BEFORE updating the address bar. Folder browse URLs share the
      // same shape as document deep links; navigating first lets URL-sync try by-path
      // (403/crash) before the explorer tab exists.
      const stableTabSlotId = currentTab?.tabSlotId || 'tab-main'
      const updatedTabs = opts.openTabs
        .filter(tab => tab.id !== 'list' || tab.id === opts.activeTabId)
        .map(tab =>
          tab.id === opts.activeTabId
            ? {
                id: 'list',
                title: 'Explorer',
                type: 'explorer' as const,
                isDirty: false,
                isPinned: false,
                tabSlotId: stableTabSlotId,
                explorerData: { folderPath },
              }
            : tab,
        )
      opts.setOpenTabs(updatedTabs)
      opts.setActiveTabId('list')
      opts.navigation.navigate(
        stableTabSlotId,
        { documentId: null, folderPath, viewMode: 'list' },
        'breadcrumb-folder-replace-current',
      )
      opts.onFolderClick?.(folderPath, true)
      const targetUrl = folderPath
        ? documentUrlForPathOrId(folderPath, opts.workspaces)
        : '/'
      opts.navigate(targetUrl, { replace: true })
    }
  }
}

/**
 * Breadcrumb segments for a document's folder path (slug-shaped labels, not ws/{hex}).
 */
export function buildDocumentFolderBreadcrumbSegments(
  options: BuildDocumentFolderBreadcrumbsOptions,
): BreadcrumbSegment[] | null {
  const pathParts = options.documentPath.split('/').filter(p => p)
  const hasLeading = (options.leadingSegments?.length ?? 0) > 0
  if (pathParts.length <= 1 && !hasLeading) {
    return null
  }

  const folderStoragePath = pathParts.length > 1 ? pathParts.slice(0, -1).join('/') : ''
  const displaySegments =
    folderStoragePath.length > 0
      ? displaySegmentsForStoragePath(folderStoragePath, options.workspaces)
      : []

  const breadcrumbSegments: BreadcrumbSegment[] = [...(options.leadingSegments ?? [])]

  for (const segment of displaySegments) {
    breadcrumbSegments.push({
      label: segment.label,
      path: segment.storagePath,
      onClick: createFolderBreadcrumbClickHandler(segment.storagePath, options),
    })
  }

  return breadcrumbSegments.length > 0 ? breadcrumbSegments : null
}

/** @deprecated Use buildDocumentFolderBreadcrumbSegments */
export function generateBreadcrumbs(
  documentPath: string,
  openTabs: OpenTab[],
  activeTabId: string,
  setOpenTabs: Dispatch<React.SetStateAction<OpenTab[]>>,
  setActiveTabId: (id: string) => void,
  navigation: PerTabNavigationApi,
  onFolderClick?: (folderPath: string) => void,
): BreadcrumbSegment[] | null {
  return buildDocumentFolderBreadcrumbSegments({
    documentPath,
    workspaces: [],
    openTabs,
    activeTabId,
    setOpenTabs,
    setActiveTabId,
    navigation,
    navigate: () => {},
    onFolderClick,
  })
}

/** Nextcloud file view uses the document header. The folder index keeps its own bar. */
export function isNextcloudDocumentTab(tab: OpenTab | undefined): boolean {
  return tab?.type === 'integration-nextcloud' && tab.integrationData?.nextcloudSurface === 'document'
}

/** Toolbar is hidden for full-bleed index / reader views that use their own chrome. */
export function shouldHideDocumentViewerToolbar(
  activeTabId: string,
  currentTab: OpenTab | undefined
): boolean {
  const isGraphView = activeTabId === 'graph' || currentTab?.type === 'graph-view'
  const isHomeTab = currentTab?.type === 'home'
  const isWorkspaceHomeTab = currentTab?.type === 'workspace-home'
  const isDeskHomeTab = currentTab?.type === 'desk-home'
  const isInboxTab = currentTab?.type === 'inbox'
  const isFlowIndexTab = currentTab?.type === 'flow-index'
  const isInboxThreadTab = currentTab?.type === 'inbox-thread'
  const isSearchView = currentTab?.type === 'search-view'
  const isPersonView = currentTab?.type === 'person-view'
  const isDriveFolderTab = currentTab?.type === 'integration-google-drive'
  const isNextcloudFolderTab =
    currentTab?.type === 'integration-nextcloud' && !isNextcloudDocumentTab(currentTab)
  const isEditHistory =
    currentTab?.type === 'edit-history' ||
    activeTabId === 'edit-history' ||
    activeTabId === 'view:edit-history'
  return (
    isGraphView ||
    isHomeTab ||
    isWorkspaceHomeTab ||
    isDeskHomeTab ||
    isInboxTab ||
    isFlowIndexTab ||
    isInboxThreadTab ||
    isSearchView ||
    isPersonView ||
    isDriveFolderTab ||
    isNextcloudFolderTab ||
    isEditHistory
  )
}

const INTEGRATION_TAB_TYPES = new Set<OpenTab['type']>([
  'integration-basecamp',
  'integration-basecamp-todo',
  'integration-basecamp-todo-list',
  'integration-basecamp-triage',
  'integration-voice-memos',
  'integration-product-board',
])

/** Limb index / object / preview - not a Maater document. */
export function isExtensionViewerTab(tab: OpenTab | undefined): boolean {
  if (!tab) return false
  return INTEGRATION_TAB_TYPES.has(tab.type) || tab.type === 'integration-google-drive-preview'
}

/**
 * Document-only overflow (outline, undo, file location, rename, archive).
 * Product board keeps document chrome; other limb surfaces do not.
 */
export function shouldHideDocumentFileActions(tab: OpenTab | undefined): boolean {
  if (!tab) return false
  if (tab.type === 'integration-product-board') return false
  if (tab.type === 'platform-skill') return true
  if (isNextcloudDocumentTab(tab)) return true
  return isExtensionViewerTab(tab)
}

export type ViewerRefreshAction = {
  label: 'Refresh view' | 'Refresh roadmap' | 'Refresh document'
}

/**
 * Overflow Refresh only when this surface has a reload handler.
 * Indexes say "Refresh view"; documents say "Refresh document".
 */
export function resolveViewerRefreshAction(
  tab: OpenTab | undefined,
): ViewerRefreshAction | null {
  if (!tab) return null
  if (
    tab.type === 'explorer' ||
    tab.type === 'home' ||
    tab.type === 'workspace-home' ||
    tab.type === 'desk-home' ||
    tab.type === 'platform-skill'
  ) {
    return null
  }
  if (tab.type === 'integration-product-board') {
    return { label: 'Refresh roadmap' }
  }
  // Todos / Projects / My stuff / project home dispatch basecamp-section-refresh
  if (tab.type === 'integration-basecamp') {
    return { label: 'Refresh view' }
  }
  // Todo object overflow already has its own Refresh item
  if (
    tab.type === 'integration-basecamp-todo' ||
    tab.type === 'integration-basecamp-todo-list' ||
    tab.type === 'integration-basecamp-triage' ||
    tab.type === 'integration-voice-memos' ||
    tab.type === 'integration-google-drive-preview' ||
    tab.type === 'integration-nextcloud'
  ) {
    return null
  }
  return { label: 'Refresh document' }
}

/** Regular content document tab (toolbar visible, not explorer/integration). */
export function isContentDocumentTab(
  tab: OpenTab | undefined,
  activeTabId: string | null | undefined,
): boolean {
  if (!activeTabId || !tab) return false
  if (shouldHideDocumentViewerToolbar(activeTabId, tab)) return false
  if (
    tab.type === 'explorer' ||
    tab.type === 'workspace-home' ||
    tab.type === 'desk-home' ||
    tab.type === 'edit-history' ||
    tab.type === 'graph-view' ||
    tab.type === 'platform-skill' ||
    tab.type === 'flow-index' ||
    INTEGRATION_TAB_TYPES.has(tab.type)
  ) {
    return false
  }
  if (
    activeTabId === 'list' ||
    activeTabId === 'graph' ||
    activeTabId === 'home' ||
    activeTabId === 'edit-history' ||
    activeTabId === 'view:edit-history' ||
    activeTabId === 'search-view' ||
    activeTabId === 'people-index' ||
    activeTabId.startsWith('person-view-') ||
    tab.type === 'person-view' ||
    isPathKeyedUnresolvedDocumentId(activeTabId) ||
    isPathKeyedUnresolvedDocumentId(tab.id) ||
    activeTabId.startsWith('basecamp-') ||
    activeTabId.startsWith('integration-') ||
    activeTabId.startsWith('home-') ||
    activeTabId.startsWith('workspace-home-')
  ) {
    return false
  }
  return true
}

/**
 * Shell-level right dock (document-viewer `showInfoPane`) - NOT Continuity list preview.
 *
 * One secondary slot per surface (preview XOR info). Never stack both.
 * - document: Document Info / Version History
 * - todo-edit: Basecamp todo edit form
 * - project: Basecamp project metadata (only when the tab is a project home with projectId)
 * - null: index / hub surfaces - Continuity preview lives in-surface (`PanelRight`)
 */
export type ShellInfoDockKind = 'document' | 'todo-edit' | 'project' | 'drive-preview' | 'nextcloud-document' | null

export function resolveShellInfoDockKind(
  tab: OpenTab | undefined,
  activeTabId?: string | null,
): ShellInfoDockKind {
  if (!tab) return null
  if (tab.type === 'platform-skill') return null
  if (tab.type === 'integration-google-drive-preview') return 'drive-preview'
  if (isNextcloudDocumentTab(tab)) return 'nextcloud-document'
  if (tab.type === 'integration-basecamp-todo') return 'todo-edit'
  if (tab.type === 'integration-basecamp' && tab.integrationData?.projectId) {
    return 'project'
  }
  // Indexes own Continuity preview. Do not also open the shell info dock.
  // Loose "unknown id => document" fallback used to give workspace-home a
  // Document Info column beside its own preview (two secondary panels).
  if (
    tab.type === 'integration-basecamp' ||
    tab.type === 'integration-basecamp-todo-list' ||
    tab.type === 'integration-basecamp-triage' ||
    tab.type === 'integration-voice-memos' ||
    tab.type === 'integration-product-board' ||
    tab.type === 'explorer' ||
    tab.type === 'home' ||
    tab.type === 'workspace-home' ||
    tab.type === 'desk-home' ||
    tab.type === 'inbox' ||
    tab.type === 'inbox-thread' ||
    tab.type === 'search-view' ||
    tab.type === 'person-view' ||
    tab.type === 'flow-index' ||
    tab.type === 'edit-history' ||
    tab.type === 'graph-view'
  ) {
    return null
  }
  if (isContentDocumentTab(tab, activeTabId ?? tab.id)) return 'document'
  return null
}

export function canToggleShellInfoDock(
  tab: OpenTab | undefined,
  activeTabId?: string | null,
): boolean {
  return resolveShellInfoDockKind(tab, activeTabId) != null
}

/** Best available path for toolbar breadcrumbs (API path, tab cache, or path-shaped tab id). */
export function resolveToolbarDocumentPath(
  currentDocumentPath: string | null,
  tab: OpenTab | undefined,
  activeTabId: string | null | undefined,
): string | null {
  if (currentDocumentPath) return currentDocumentPath
  if (tab?.filePath) return tab.filePath
  if (activeTabId?.includes('/')) return activeTabId
  return null
}

/** Keep breadcrumb row height while path metadata is still loading. */
export function shouldReserveDocumentBreadcrumbRow(
  tab: OpenTab | undefined,
  activeTabId: string | null | undefined,
  documentPath: string | null,
  options?: { basecampTodoId?: string | null; pathFetchPending?: boolean },
): boolean {
  if (!isContentDocumentTab(tab, activeTabId)) return false
  if (documentPath) {
    const parts = documentPath.split('/').filter(Boolean)
    if (parts.length > 1) return true
    if (options?.basecampTodoId) return true
    return false
  }
  return Boolean(options?.pathFetchPending)
}

/** Whether the current tab can be pinned to sidebar shortcuts (toolbar overflow menu). */
export function isDocumentPinnableTab(
  activeTabId: string | null | undefined,
  tab: OpenTab | undefined
): boolean {
  if (!tab) return false
  const t = tab.type
  if (
    t === 'integration-basecamp-todo' ||
    t === 'integration-basecamp-triage' ||
    t === 'integration-basecamp' ||
    t === 'integration-product-board' ||
    t === 'explorer' ||
    t === 'home'
  ) {
    return false
  }
  if (String(activeTabId ?? '').startsWith('new-')) return false
  return true
}
